// src/pages/PaymentGateway/AddPaymentGateway.jsx
import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../services/api';
import Button from '../../components/common/Button/Button';
import styles from './AddPaymentGateway.module.css';

const initialFormData = {
  transaction_id: '',
  payment_id: '',
  entity_type: '',
  amount: '',
  currency: '',
  base_amount: '',
  status: '',
  order_id: '',
  rp_invoice_id: '',
  international: '',
  method: '',
  amount_refunded: '',
  amount_transferred: '',
  refund_status: '',
  captured: '',
  description: '',
  card_id: '',
  bank: '',
  wallet: '',
  vpa: '',
  email: '',
  contact: '',
  customer_id: '',
  token_id: '',
  fee: '',
  tax: '',
  error_code: '',
  error_description: '',
  error_source: '',
  error_step: '',
  error_reason: '',
  acquirer_data_rrn: '',
  acquirer_data_upi_transaction_id: '',
  created_at_rp: '',
  reward: '',
  upi_vpa: '',
  upi_flow: '',
  created_at: '',
  updated_at: '',
  service_provider: '',
  service_provider_tid: '',
  recurring_type: '',
  transaction_date: '',
  transaction_time: '',
  transaction_type: '',
  transaction_status: '',
  commission_rate: '',
  commission_amount: '',
  net_amount: '',
  bank_settlement_date: '',
  user_name: '',
};

const numericFields = [
  'amount',
  'base_amount',
  'amount_refunded',
  'amount_transferred',
  'fee',
  'tax',
  'commission_rate',
  'commission_amount',
  'net_amount',
];

const textareaFields = new Set([
  'description',
  'error_description',
  'error_reason',
]);

const dateFields = new Set([
  'transaction_date',
  'bank_settlement_date',
]);

const dateTimeFields = new Set([
  'created_at',
  'updated_at',
  'created_at_rp',
]);

const emailFields = new Set(['email']);
const telFields = new Set(['contact']);
const wideFields = new Set([
  'description',
  'error_description',
  'error_reason',
]);

const fieldSections = [
  {
    title: 'Payment Details',
    description: 'Core gateway identifiers, payment values, and transaction state.',
    fields: [
      'transaction_id',
      'payment_id',
      'entity_type',
      'amount',
      'currency',
      'base_amount',
      'status',
      'order_id',
      'rp_invoice_id',
      'international',
      'method',
      'captured',
      'description',
    ],
  },
  {
    title: 'Settlement And Charges',
    description: 'Refunds, transfer values, commission, tax, and settlement details.',
    fields: [
      'amount_refunded',
      'amount_transferred',
      'refund_status',
      'fee',
      'tax',
      'commission_rate',
      'commission_amount',
      'net_amount',
      'bank_settlement_date',
      'reward',
    ],
  },
  {
    title: 'Customer And Payment Method',
    description: 'Customer identity and method-specific details such as bank, wallet, and UPI.',
    fields: [
      'card_id',
      'bank',
      'wallet',
      'vpa',
      'upi_vpa',
      'upi_flow',
      'email',
      'contact',
      'customer_id',
      'token_id',
      'user_name',
    ],
  },
  {
    title: 'Error And Acquirer Data',
    description: 'Gateway failure metadata and acquirer reference values.',
    fields: [
      'error_code',
      'error_description',
      'error_source',
      'error_step',
      'error_reason',
      'acquirer_data_rrn',
      'acquirer_data_upi_transaction_id',
    ],
  },
  {
    title: 'Platform Metadata',
    description: 'Internal platform mapping, service provider info, and timestamps.',
    fields: [
      'created_at_rp',
      'created_at',
      'updated_at',
      'service_provider',
      'service_provider_tid',
      'recurring_type',
      'transaction_date',
      'transaction_time',
      'transaction_type',
      'transaction_status',
    ],
  },
];

const formatDateValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const formatDateTimeValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const AddPaymentGateway = ({ gatewayId, onSubmitted, onCancel }) => {
  const id = gatewayId;
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetchingDetail, setFetchingDetail] = useState(isEditing);
  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!isEditing) return;

    setFetchingDetail(true);
    api
      .get(`/paymentgateway/${id}`)
      .then((response) => {
        let dataSource = response?.result || response?.data || response || {};

        if (typeof dataSource === 'string') {
          try {
            dataSource = JSON.parse(dataSource);
          } catch (error) {
            console.error('Invalid payment gateway response JSON:', error);
          }
        }

        const data = Array.isArray(dataSource) ? (dataSource[0] || {}) : dataSource;

        if (!data || Object.keys(data).length === 0) {
          toast.error('Payment gateway record not found');
          if (onCancel) onCancel();
          return;
        }

        const cleaned = { ...data };
        Object.keys(initialFormData).forEach((key) => {
          if (cleaned[key] === null || cleaned[key] === undefined) {
            cleaned[key] = '';
          }
        });

        dateFields.forEach((field) => {
          cleaned[field] = formatDateValue(cleaned[field]);
        });

        dateTimeFields.forEach((field) => {
          cleaned[field] = formatDateTimeValue(cleaned[field]);
        });

        numericFields.forEach((field) => {
          if (cleaned[field] !== '') {
            cleaned[field] = String(cleaned[field]);
          }
        });

        setFormData((prev) => ({ ...prev, ...cleaned }));
      })
      .catch((err) => {
        console.error('Failed to fetch payment gateway record', err);
        toast.error('Could not load payment gateway record.');
      })
      .finally(() => setFetchingDetail(false));
  }, [id, isEditing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { ...formData };
      numericFields.forEach((field) => {
        if (payload[field] === '' || payload[field] === null) {
          payload[field] = null;
        } else {
          const num = parseFloat(payload[field]);
          payload[field] = Number.isNaN(num) ? null : num;
        }
      });

      if (isEditing) {
        await api.put(`/paymentgateway/${id}`, payload);
        toast.success('Payment gateway record updated successfully!');
      } else {
        await api.post('/paymentgateway', payload);
        toast.success('Payment gateway record created successfully!');
      }

      setTimeout(() => {
        if (onSubmitted) onSubmitted();
      }, 1000);
    } catch (err) {
      console.error('Error saving payment gateway record:', err);
      const msg = err?.data?.message || err?.message || 'Failed to save record';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getInputType = (key) => {
    if (numericFields.includes(key)) return 'number';
    if (emailFields.has(key)) return 'email';
    if (telFields.has(key)) return 'tel';
    if (dateFields.has(key)) return 'date';
    if (dateTimeFields.has(key)) return 'datetime-local';
    return 'text';
  };

  return (
    <div className={styles.container}>
      <ToastContainer position="top-center" autoClose={2000} />
      <div className={styles.card}>


        {fetchingDetail ? (
          <div className={styles.loadingState}>
            Loading payment gateway details...
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className={styles.sections}>
              {fieldSections.map((section) => (
                <section className={styles.sectionCard} key={section.title}>
                  <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>{section.title}</h3>
                    <p className={styles.sectionDescription}>{section.description}</p>
                  </div>

                  <div className={styles.formGrid}>
                    {section.fields.map((key) => (
                      <div
                        className={`${styles.formGroup} ${wideFields.has(key) ? styles.fullWidth : ''}`}
                        key={key}
                      >
                        <label className={styles.label}>{key.replace(/_/g, ' ')}</label>
                        {textareaFields.has(key) ? (
                          <textarea
                            name={key}
                            value={formData[key]}
                            onChange={handleChange}
                            placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                            className={`${styles.input} ${styles.textarea}`}
                            rows={4}
                          />
                        ) : (
                          <input
                            type={getInputType(key)}
                            name={key}
                            value={formData[key]}
                            onChange={handleChange}
                            placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                            className={styles.input}
                            step={numericFields.includes(key) ? 'any' : undefined}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className={styles.actions}>
              <Button
                variant="secondary"
                onClick={onCancel}
                type="button"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Saving...' : isEditing ? 'Update Record' : 'Save Record'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddPaymentGateway;
